import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Microphone,
  Camera,
  House,
  ArrowRight,
  Sparkle,
} from "@phosphor-icons/react";

/**
 * LiveTryThree — sezione "prova senza registrarti" con 3 opzioni reali.
 *
 * iter v31 r26 — User: «le cose che possono provare senza registrarsi
 * erano le foto (migliora) e la planimetria e il sopralluogo, giusto?».
 * Sì: il backend espone già /api/public/demo/voice|enhance|furnish-plan
 * e c'è /prova (Demo.jsx) che li avvolge. La nuova landing aveva una
 * sola entry (LiveTryItCard, solo /enhance). Qui le mostriamo tutte e
 * tre come 3 tile, ognuna deep-linka a /prova?demo=... col demo
 * pre-selezionato.
 *
 * Limite: 3 prove gratuite/IP/giorno per ciascun endpoint (rate limited
 * lato backend in routes/demo.py).
 */

// iter v31 r37 — User chiarisce: cover 1 = anteprima dell'app sopralluogo
// (replica della schermata SOPRALLUOGO LIVE con waveform/trascrizione/foto/
// scheda pronta che gli avevo già fatto in screenshot). Cover 2 = una vera
// foto migliorata. Cover 3 = una vera pianta arredata. Niente più
// "/prova?demo=..." screenshot generici.
// iter v32 r37f — User M1432: su mobile le card sembravano illustrazioni
// statiche, non bottoni tappabili. Label troncate ("SOPRALLUO..."), body
// nascosto su mobile (hidden sm:block), zero affordance. Layout mobile
// rifatto a 1-colonna orizzontale (foto a sx + body a dx con CTA «Prova
// ora →» sempre visibile). Desktop 3-colonne invariato.
const DEMOS = [
  {
    id: "sopralluogo",
    Icon: Microphone,
    label: "Sopralluogo",
    sub: "Detta una descrizione: l'annuncio è pronto subito, già impaginato.",
    cover: "/landing/demo-screens/sopralluogo.jpg",
    objectPos: "top",
    accent: "#7C8B6E",
  },
  {
    id: "foto",
    Icon: Camera,
    label: "Migliora foto",
    sub: "Carica una foto col telefono. Luce, colori, prospettiva sistemati in pochi secondi.",
    cover: "/landing/sala-original-after.jpg",
    objectPos: "center",
    accent: "#B57C5E",
  },
  {
    id: "pianta",
    Icon: House,
    label: "Arreda pianta",
    sub: "Una planimetria catastale: la trasformiamo in render arredato.",
    cover: "/landing/pianta-after.jpg",
    objectPos: "center",
    accent: "#5A6A4D",
  },
];

export default function LiveTryThree() {
  const navigate = useNavigate();
  return (
    <section
      className="bg-[#FAFAF7] border-y border-[#EDEAE2] py-10 sm:py-14"
      data-testid="live-try-three-section"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-300/50 mb-3">
            <Sparkle size={11} weight="fill" className="text-amber-600" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-800">
              Senza registrarti
            </span>
          </div>
          <h2
            className="font-editorial text-[24px] sm:text-[34px] lg:text-[40px] leading-[1.1] tracking-tight text-[#1C1C1A]"
            data-testid="live-try-three-title"
          >
            Provala adesso,{" "}
            <span className="italic text-[#7C8B6E]">l&apos;applicazione.</span>
          </h2>
          <p className="text-[13px] sm:text-[14px] text-[#6B6A65] mt-3 leading-snug">
            Tocca una card · senza account, senza carta. <strong>3 prove gratis</strong> per dispositivo.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {DEMOS.map((d) => {
            const { Icon } = d;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => navigate(`/prova?demo=${d.id}`)}
                className="group relative text-left rounded-xl bg-white border border-[#1C1C1A]/10 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-18px_rgba(28,28,26,0.22)] active:scale-[0.99] flex sm:block"
                data-testid={`live-try-${d.id}`}
              >
                {/* Cover */}
                <div className="relative w-[42%] sm:w-full flex-shrink-0 aspect-square sm:aspect-[16/10] overflow-hidden">
                  <img
                    src={d.cover}
                    alt={d.label}
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${
                      d.objectPos === "top" ? "object-top" : "object-center"
                    }`}
                    loading="lazy"
                    draggable={false}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none hidden sm:block"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 50%, rgba(28,28,26,0.6) 100%)",
                    }}
                  />
                  {/* Desktop: badge in cover bottom */}
                  <div className="hidden sm:inline-flex absolute bottom-2 left-2 right-2 items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur">
                    <Icon size={11} weight="fill" style={{ color: d.accent }} />
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#1C1C1A]">
                      {d.label}
                    </span>
                  </div>
                </div>

                {/* Body — visibile SEMPRE (mobile a destra, desktop sotto) */}
                <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between">
                  <div>
                    {/* Mobile: icon + label inline (sostituisce il badge cover) */}
                    <div className="sm:hidden flex items-center gap-1.5 mb-1">
                      <Icon size={13} weight="fill" style={{ color: d.accent }} />
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1C1C1A]">
                        {d.label}
                      </span>
                    </div>
                    <p className="text-[12px] sm:text-[12.5px] text-[#3F3D38] leading-snug line-clamp-3">
                      {d.sub}
                    </p>
                  </div>
                  {/* CTA visibile sempre */}
                  <div
                    className="mt-2 sm:mt-3 inline-flex items-center gap-1 text-[12px] font-bold transition-colors"
                    style={{ color: d.accent }}
                  >
                    Prova ora
                    <ArrowRight
                      size={12}
                      weight="bold"
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
